/**
 * USE FACTURACION - Hooks para gestión de facturas y pagos
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  getFactura,
  createFactura,
  anularFactura,
  registrarPago,
  getOrdenesDisponibles,
  getOrdenPorNumero,
  getMetodosPago,
  getCAI,
  createCAI,
  updateCAI,
  deactivateCAI,
  activateCAI,
  deleteCAI,
  type FacturaCreateData,
  type PagoCreateData,
  type CAICreateData,
} from "@/services/facturacion";

// Re-export functions and constants for useServerDataTable
export {
  getFacturas,
  getCAIs,
  getListasPrecios,
  getPreciosExamen,
  TIPOS_LISTA_PRECIO,
  ESTADOS_FACTURA,
} from "@/services/facturacion";

// Re-export types
export type {
  Factura,
  FacturaListItem,
  FacturaCreateData,
  FacturaDetalle,
  FacturaOrdenResumen,
  FacturaCliente,
  PagoFactura,
  PagoResult,
  PagoCreateData,
  OrdenDisponible,
  OrdenDisponibleExamen,
  MetodoPago,
  EstadoFactura,
  TipoDocumentoFiscal,
  TipoMetodoPago,
  TipoPago,
  CAI,
  CAIListItem,
  CAICreateData,
} from "@/services/facturacion";

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface CreateFacturaMutationCallbacks {
  onSuccess?: (factura: import("@/services/facturacion").Factura) => void;
  onError?: (error: Error) => void;
}

// =============================================================================
// QUERIES
// =============================================================================

export function useFactura(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.facturas.detail(id),
    queryFn: () => getFactura(id),
    enabled: options?.enabled ?? true,
    staleTime: 0, // siempre considerar stale para ver pagos actualizados
    refetchOnMount: true, // re-fetch al abrir el detail sheet
  });
}

export function useMetodosPago() {
  return useQuery({
    queryKey: queryKeys.metodosPago.all,
    queryFn: () => getMetodosPago({ page: 1, page_size: 100 }),
    staleTime: 10 * 60 * 1000, // 10 min - rarely changes
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

export function useCreateFactura(callbacks?: CreateFacturaMutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FacturaCreateData) => createFactura(data),
    onSuccess: (factura) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facturas.all });
      callbacks?.onSuccess?.(factura);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useAnularFactura(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => anularFactura(id, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facturas.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useRegistrarPago(callbacks?: {
  onSuccess?: (result: import("@/services/facturacion").PagoResult) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PagoCreateData }) => registrarPago(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.facturas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordenes.all });
      callbacks?.onSuccess?.(result);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useOrdenesDisponibles(
  pacienteId: number | null,
  sucursalId: number | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.facturas.ordenesDisponibles(pacienteId ?? 0, sucursalId ?? 0),
    queryFn: () => getOrdenesDisponibles({ pacienteId: pacienteId!, sucursalId: sucursalId! }),
    enabled: (options?.enabled ?? true) && pacienteId != null && sucursalId != null,
    staleTime: 30 * 1000,
  });
}

export function useOrdenesDisponiblesPorCliente(
  clienteId: number | null,
  sucursalId: number | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.facturas.ordenesDisponiblesPorCliente(clienteId ?? 0, sucursalId ?? 0),
    queryFn: () => getOrdenesDisponibles({ clienteId: clienteId!, sucursalId: sucursalId! }),
    enabled: (options?.enabled ?? true) && clienteId != null && sucursalId != null,
    staleTime: 30 * 1000,
  });
}

export function useOrdenPorNumero(
  numero: string,
  sucursalId: number | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.facturas.ordenPorNumero(numero, sucursalId ?? 0),
    queryFn: () => getOrdenPorNumero(numero, sucursalId!),
    enabled: numero.length >= 3 && sucursalId != null && (options?.enabled ?? true),
    staleTime: 15_000,
  });
}

// =============================================================================
// CAI QUERIES
// =============================================================================

export function useCAI(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.cais.detail(id),
    queryFn: () => getCAI(id),
    enabled: options?.enabled ?? true,
  });
}

// =============================================================================
// CAI MUTATIONS
// =============================================================================

export function useCreateCAI(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CAICreateData) => createCAI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cais.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateCAI(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CAICreateData }) => updateCAI(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cais.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateCAI(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateCAI(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cais.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivateCAI(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateCAI(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cais.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteCAI(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCAI(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cais.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// LISTA PRECIO - Imports
// =============================================================================

import {
  getListasPrecios,
  getListaPrecio,
  getPreciosExamen,
  createListaPrecio,
  updateListaPrecio,
  deactivateListaPrecio,
  activateListaPrecio,
  deleteListaPrecio,
  createPrecioExamen,
  updatePrecioExamen,
  deletePrecioExamen,
  type ListaPrecioCreateData,
  type PrecioExamenCreateData,
} from "@/services/facturacion";

// Re-export ListaPrecio types
export type {
  ListaPrecio,
  ListaPrecioCreateData,
  TipoListaPrecio,
  PrecioExamen,
  PrecioExamenCreateData,
} from "@/services/facturacion";

// =============================================================================
// LISTA PRECIO QUERIES
// =============================================================================

export function useListasPrecios(onlyActive = true) {
  return useQuery({
    queryKey: [...queryKeys.listasPrecios.all, { onlyActive }],
    queryFn: () =>
      getListasPrecios({ page: 1, page_size: 100, ...(onlyActive ? { is_active: "true" } : {}) }),
    select: (data) => data.results,
    staleTime: 5 * 60 * 1000,
  });
}

export function useListaPrecio(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.listasPrecios.detail(id),
    queryFn: () => getListaPrecio(id),
    enabled: options?.enabled ?? true,
  });
}

// =============================================================================
// LISTA PRECIO MUTATIONS
// =============================================================================

export function useCreateListaPrecio(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ListaPrecioCreateData) => createListaPrecio(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listasPrecios.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateListaPrecio(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ListaPrecioCreateData }) =>
      updateListaPrecio(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listasPrecios.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateListaPrecio(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateListaPrecio(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listasPrecios.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivateListaPrecio(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateListaPrecio(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listasPrecios.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteListaPrecio(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteListaPrecio(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listasPrecios.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// PRECIO EXAMEN QUERIES
// =============================================================================

export function usePreciosExamen(
  listaPrecioId: number | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.preciosExamen.list({ lista: listaPrecioId }),
    queryFn: () => getPreciosExamen({ lista: listaPrecioId!, page: 1, page_size: 100 }),
    enabled: (options?.enabled ?? true) && !!listaPrecioId,
  });
}

// =============================================================================
// PRECIO EXAMEN MUTATIONS
// =============================================================================

export function useCreatePrecioExamen(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PrecioExamenCreateData) => createPrecioExamen(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.preciosExamen.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdatePrecioExamen(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PrecioExamenCreateData }) =>
      updatePrecioExamen(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.preciosExamen.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeletePrecioExamen(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePrecioExamen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.preciosExamen.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}
